import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../utils/authHeader";
import { getApiErrorMessage } from "../utils/apiError";
import { AnalyticsSummary, AnalyticsWindow } from "../types/analytics";

export interface UseAnalyticsSummaryReturn {
    data: AnalyticsSummary | null;
    loading: boolean;
    error: string | null;
    window: AnalyticsWindow;
    setWindow: (w: AnalyticsWindow) => void;
    refresh: () => void;
}

/**
 * Fetches the current user's activity summary for the selected window.
 * Always reads from the api (never Redux) so it is correct under impersonation.
 */
export const useAnalyticsSummary = (
    initialWindow: AnalyticsWindow = 7
): UseAnalyticsSummaryReturn => {
    const [window, setWindow] = useState<AnalyticsWindow>(initialWindow);
    const [data, setData] = useState<AnalyticsSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [tick, setTick] = useState(0);

    const refresh = useCallback((): void => setTick((t) => t + 1), []);

    useEffect(() => {
        let active = true;
        const load = async (): Promise<void> => {
            setLoading(true);
            setError(null);
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
                const res = await axios.get<AnalyticsSummary>(
                    `${backendUrl}/analytics/summary`,
                    { headers, params: { window } }
                );
                if (active) setData(res.data);
            } catch (err) {
                // Clear stale data on a failed fetch - otherwise a window switch that
                // fails leaves the tabs on the new window while every number on screen
                // still belongs to the old one, with nothing on screen saying so.
                if (active) {
                    setData(null);
                    setError(getApiErrorMessage(err, "We couldn't load your analytics. Please try again."));
                }
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [window, tick]);

    return { data, loading, error, window, setWindow, refresh };
};
